import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { Sparkles, X, Mic, MicOff, Send, RotateCcw } from "lucide-react";
import { Api } from "../lib/api.js";
import { linkForItem } from "../lib/linkForItem.js";

/**
 * "Help me find something" — a multi-turn concierge. Opens as a modal
 * panel; the viewer can talk OR type. Claude either asks one follow-up
 * question or commits to filters; on commit the server returns a row of
 * picks rendered inline.
 *
 * Voice input uses the standard Web Speech API. WallaceTV's APK already
 * grants the mic when the WebView asks; in a regular browser the first
 * tap triggers the standard mic permission prompt.
 */

interface MoodItem {
  id: string;
  kind: string;
  title: string;
  thumbnail_url: string | null;
  backdrop_url: string | null;
  tmdb_rating: number | null;
  tmdb_genres: string[] | null;
  tmdb_overview: string | null;
  duration_s: number | null;
  category: string | null;
  release_year: number | null;
  episode_count?: number;
}

type Turn = { role: "user" | "assistant"; content: string };

export function MoodFinder({ profileId }: { profileId: string }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState("");
  const [picks, setPicks] = useState<{ items: MoodItem[]; blurb: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  function reset(): void {
    setTurns([]);
    setDraft("");
    setPicks(null);
    setError(null);
    setCurrentOptions(null);
  }

  const send = useMutation({
    mutationFn: (history: Turn[]) =>
      Api.mood.turn({ profile_id: profileId, conversation: history }),
  });

  const [currentOptions, setCurrentOptions] = useState<string[] | null>(null);

  async function submit(text: string): Promise<void> {
    const userText = text.trim();
    if (!userText) return;
    setDraft("");
    setError(null);
    setCurrentOptions(null);
    const nextHistory: Turn[] = [...turns, { role: "user", content: userText }];
    setTurns(nextHistory);
    try {
      const r = await send.mutateAsync(nextHistory);
      if (r.phase === "ask") {
        setTurns([...nextHistory, { role: "assistant", content: r.question }]);
        setCurrentOptions(r.options ?? null);
      } else if (r.phase === "reply") {
        setTurns([...nextHistory, { role: "assistant", content: r.message }]);
        setCurrentOptions(null);
      } else {
        setPicks({ items: r.items, blurb: r.blurb });
        setTurns([
          ...nextHistory,
          { role: "assistant", content: r.blurb ?? `Here are ${r.items.length} picks.` },
        ]);
        setCurrentOptions(null);
      }
    } catch (err) {
      setError((err as Error).message);
    }
  }

  // Auto-scroll the transcript to the bottom when a new turn lands.
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [turns, picks]);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="pill bg-vermilion border-goldHi"
        title="Talk to Wallace and get a custom suggestion"
      >
        <Sparkles size={14} /> Help me find something
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-ink/90 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative z-10 card w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
            <header className="px-6 py-4 border-b border-hairline flex items-center justify-between">
              <div className="flex items-baseline gap-3">
                <Sparkles className="text-goldHi" size={18} />
                <div>
                  <div className="section-label text-gold">CONCIERGE</div>
                  <h2 className="font-display text-ivory text-xl mt-0.5">What are you in the mood for?</h2>
                </div>
              </div>
              <div className="flex gap-1">
                <button onClick={reset} className="pill-ghost p-2" title="Start over" aria-label="Start over">
                  <RotateCcw size={16} />
                </button>
                <button onClick={() => setOpen(false)} className="pill-ghost p-2" aria-label="Close">
                  <X size={16} />
                </button>
              </div>
            </header>

            <div ref={listRef} className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {turns.length === 0 && !send.isPending && (
                <ExamplePrompts onPick={(t) => void submit(t)} />
              )}
              {turns.map((t, i) => (
                <TurnBubble key={i} turn={t} />
              ))}
              {currentOptions && currentOptions.length > 0 && !send.isPending && (
                <div className="flex flex-wrap gap-2 justify-start">
                  {currentOptions.map((opt) => (
                    <button
                      key={opt}
                      onClick={() => void submit(opt)}
                      className="pill-ghost border border-gold/40 text-sm hover:text-gold hover:bg-vermilion/10"
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}
              {send.isPending && (
                <div className="flex items-center gap-2 text-parchment text-sm">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-gold animate-pulse" style={{ animationDelay: "120ms" }} />
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-gold animate-pulse" style={{ animationDelay: "240ms" }} />
                  <span className="ml-2">Wallace is thinking…</span>
                </div>
              )}
              {picks && (
                <PicksGrid items={picks.items} onClose={() => setOpen(false)} />
              )}
              {error && (
                <div className="text-vermilionHi text-sm">{error}</div>
              )}
            </div>

            <footer className="px-6 py-4 border-t border-hairline">
              <Composer
                value={draft}
                setValue={setDraft}
                onSubmit={() => void submit(draft)}
                disabled={send.isPending}
              />
            </footer>
          </div>
        </div>
      )}
    </>
  );
}

function ExamplePrompts({ onPick }: { onPick: (text: string) => void }): JSX.Element {
  const examples = [
    "a feel-good comedy from the 80s",
    "something short and snappy under an hour",
    "cerebral sci-fi like Arrival",
    "a documentary I can sleep to",
    "a thriller from the 90s",
  ];
  return (
    <div>
      <div className="text-parchment text-sm mb-3">
        Say something like…
      </div>
      <div className="flex flex-wrap gap-2">
        {examples.map((e) => (
          <button
            key={e}
            onClick={() => onPick(e)}
            className="pill-ghost text-left text-sm hover:text-gold border border-hairline"
          >
            {e}
          </button>
        ))}
      </div>
    </div>
  );
}

function TurnBubble({ turn }: { turn: Turn }): JSX.Element {
  const isUser = turn.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[80%] px-3 py-2 rounded-lg text-sm leading-relaxed ${
          isUser
            ? "bg-vermilion/30 border border-vermilion/50 text-ivory"
            : "bg-elev/70 border border-hairline text-parchment"
        }`}
      >
        {turn.content}
      </div>
    </div>
  );
}

function PicksGrid({ items, onClose }: { items: MoodItem[]; onClose: () => void }): JSX.Element {
  if (items.length === 0) {
    return (
      <div className="card-elev p-6 text-parchment text-sm">
        Nothing in your catalog matched closely. Try widening it — drop the
        year filter, soften the rating, or pick a different genre.
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
      {items.map((it) => {
        const rating = typeof it.tmdb_rating === "number"
          ? Math.round(it.tmdb_rating * 10) / 10 : null;
        return (
          <Link
            key={it.id}
            to={linkForItem({ id: it.id, kind: it.kind, category: it.category })}
            onClick={onClose}
            className="card group overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-goldHi hover:scale-[1.03] transition-transform duration-200 ease-spring"
          >
            <div className="aspect-[2/3] bg-elev relative">
              {it.thumbnail_url && (
                <img
                  src={it.thumbnail_url}
                  alt=""
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  className="w-full h-full object-cover"
                />
              )}
              {rating !== null && (
                <span className="absolute top-2 left-2 px-1.5 py-0.5 rounded text-[11px] bg-ink/85 border border-gold/40 font-display tabular-nums text-goldHi">
                  ★ {rating}
                </span>
              )}
              {it.release_year && (
                <span className="absolute top-2 right-2 px-1.5 py-0.5 rounded text-[10px] bg-ink/85 border border-hairline font-display tabular-nums text-parchment">
                  {it.release_year}
                </span>
              )}
            </div>
            <div className="p-2">
              <div className="text-ivory text-xs leading-tight line-clamp-2 min-h-[2.2em]">
                {it.title}
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

// ---- Composer (text input + mic) ------------------------------------------

interface SpeechRecognitionEvent { results: { 0: { transcript: string } }[] }
type SpeechRecognition = EventTarget & {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((e: SpeechRecognitionEvent) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
};
declare global {
  interface Window {
    SpeechRecognition?: { new(): SpeechRecognition };
    webkitSpeechRecognition?: { new(): SpeechRecognition };
  }
}

function Composer({
  value,
  setValue,
  onSubmit,
  disabled,
}: {
  value: string;
  setValue: (v: string) => void;
  onSubmit: () => void;
  disabled: boolean;
}): JSX.Element {
  const [listening, setListening] = useState(false);
  const recogRef = useRef<SpeechRecognition | null>(null);
  const speechAvailable =
    typeof window !== "undefined" &&
    !!(window.SpeechRecognition ?? window.webkitSpeechRecognition);

  function startListening(): void {
    const ctor = window.SpeechRecognition ?? window.webkitSpeechRecognition;
    if (!ctor) return;
    const r = new ctor();
    r.continuous = false;
    r.interimResults = false;
    r.lang = "en-US";
    r.onresult = (e) => {
      const transcript = e.results[0]?.[0]?.transcript ?? "";
      if (transcript) setValue(transcript);
    };
    r.onerror = () => setListening(false);
    r.onend = () => setListening(false);
    recogRef.current = r;
    setListening(true);
    r.start();
  }
  function stopListening(): void {
    recogRef.current?.stop();
    setListening(false);
  }

  return (
    <div className="flex items-center gap-2">
      {speechAvailable && (
        <button
          type="button"
          onClick={listening ? stopListening : startListening}
          className={`pill-ghost p-2 ${listening ? "text-vermilionHi" : "text-parchment"}`}
          aria-label={listening ? "Stop listening" : "Talk to Wallace"}
          title={listening ? "Stop listening" : "Talk to Wallace"}
          disabled={disabled}
        >
          {listening ? <MicOff size={16} /> : <Mic size={16} />}
        </button>
      )}
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSubmit(); } }}
        placeholder={listening ? "Listening…" : "Talk or type — \"a comedy from the 80s\""}
        className="flex-1 bg-ink/60 border border-hairline rounded-md px-3 py-2 text-ivory focus:border-gold outline-none text-sm"
        autoFocus
        disabled={disabled}
      />
      <button
        type="button"
        onClick={onSubmit}
        disabled={disabled || value.trim().length === 0}
        className="pill"
      >
        <Send size={14} /> Send
      </button>
    </div>
  );
}
